import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { sendChatMessage } from '../api/ai';
import ReactMarkdown from 'react-markdown';

const WELCOME_MESSAGE = {
  role: 'ai',
  parts: "Bonjour ! Je suis l'assistant IA de l'IUGB 🎓\n\nJe peux vous renseigner sur :\n• 🏢 Les règlements et horaires de la résidence\n• 🚐 Les navettes (Shuttle)\n• 📚 Vos questions de cours et révisions\n• 💡 Et tout autre sujet d'ordre général !\n\nComment puis-je vous aider aujourd'hui ?",
};

// ── Typing indicator ───────────────────────────────────────────
function TypingDots() {
  return (
    <div className="chatbot-bubble chatbot-bubble-ai">
      <div className="chatbot-typing">
        <span/><span/><span/>
      </div>
    </div>
  );
}

// ── Message bubble ─────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  
  const handleTTS = () => {
    if (!window.speechSynthesis) return alert("Votre navigateur ne supporte pas la synthèse vocale.");
    
    // Si on lit déjà, on annule
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }
    
    // 1. Nettoyer le markdown simple
    let cleanText = String(msg.parts).replace(/[*#_`]/g, '');
    
    // 2. Nettoyer les emojis etc (conservation des mots, ponctuation, espaces)
    cleanText = cleanText.replace(/[^\p{L}\p{N}\p{P}\p{Z}]/gu, '');
    
    // 3. Remplacer les sauts de ligne par des virgules pour que la voix marque une pause claire
    cleanText = cleanText.replace(/\n/g, ', ');

    const utterance = new SpeechSynthesisUtterance(cleanText);
    
    // Détection basique de la langue via les mots les plus utilisés
    const t = cleanText.toLowerCase();
    const enCount = (t.match(/\b(the|is|in|and|to|of|it|you|this|that|are|can)\b/g) || []).length;
    const frCount = (t.match(/\b(le|la|les|de|et|est|vous|dans|ce|cette|pour|sont|peut)\b/g) || []).length;
    utterance.lang = enCount > frCount ? 'en-US' : 'fr-FR';
    
    utterance.rate = 1.05; // Augmenté pour correspondre à la fluidité parfaite
    utterance.pitch = 1.05; // Légèrement plus doux
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className={`chatbot-bubble chatbot-bubble-${isUser ? 'user' : 'ai'}`}>
      <ReactMarkdown
        components={{
          p: ({node, ...props}) => <p style={{margin: '0 0 8px 0', lineHeight: 1.5}} {...props} />,
          strong: ({node, ...props}) => <strong style={{fontWeight: 700}} {...props} />,
          em: ({node, ...props}) => <em style={{fontStyle: 'italic'}} {...props} />,
        }}
      >
        {String(msg.parts)}
      </ReactMarkdown>
      {!isUser && (
        <button 
          className="chatbot-tts-btn" 
          onClick={handleTTS}
          title="Écouter la réponse"
          aria-label="Écouter la réponse"
        >
          🔊
        </button>
      )}
    </div>
  );
}

// ── Main Chatbot Widget ────────────────────────────────────────
export default function AiChatbot() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isListening, setIsListening] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  const scrollToBottom = () => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  useEffect(() => { if (open) { scrollToBottom(); inputRef.current?.focus(); } }, [open]);
  useEffect(() => { scrollToBottom(); }, [messages, loading]);

  // Initialisation Web Speech API
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.lang = 'fr-FR';
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput((prev) => (prev ? prev + ' ' + transcript : transcript));
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error", event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }
  }, []);

  // Uniquement pour les étudiants - Déplacé après les hooks pour respecter les règles de React
  if (!user || user.role !== 'student') return null;

  const toggleListen = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      if (recognitionRef.current) {
        recognitionRef.current.start();
        setIsListening(true);
      } else {
        alert("Votre navigateur ne supporte pas la reconnaissance vocale.");
      }
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', parts: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError('');

    try {
      // Construire l'historique pour Gemini (exclure le message de bienvenue)
      const history = messages
        .filter((m) => m !== WELCOME_MESSAGE)
        .map((m) => ({ role: m.role, parts: m.parts }));

      const { reply } = await sendChatMessage(text, history);
      setMessages((prev) => [...prev, { role: 'ai', parts: reply }]);
    } catch (e) {
      const msg = e?.response?.data?.error || 'Erreur de connexion. Réessayez.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  return (
    <>
      {/* Floating button */}
      <button
        id="ai-chatbot-fab"
        className={`chatbot-fab${open ? ' chatbot-fab-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Assistant IA"
        title="Assistant IA"
      >
        {open ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="10" rx="2"/>
            <circle cx="12" cy="5" r="2"/><line x1="12" y1="7" x2="12" y2="11"/>
            <line x1="8" y1="15" x2="8" y2="15" strokeWidth="3"/><line x1="16" y1="15" x2="16" y2="15" strokeWidth="3"/>
          </svg>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="chatbot-panel" id="ai-chatbot-panel">
          {/* Header */}
          <div className="chatbot-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <div className="chatbot-avatar">🤖</div>
              <div>
                <div className="chatbot-header-title">Assistant IUGB</div>
                <div className="chatbot-header-sub">Disponible 24h/24</div>
              </div>
            </div>
            <button className="chatbot-close" onClick={() => setOpen(false)} aria-label="Fermer">✕</button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg, i) => <MessageBubble key={i} msg={msg} />)}
            {loading && <TypingDots />}
            {error && (
              <div className="chatbot-error">{error}</div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="chatbot-input-area">
            <textarea
              ref={inputRef}
              className="chatbot-input"
              placeholder="Posez votre question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              maxLength={1000}
              id="ai-chatbot-input"
            />
            {input.trim() === '' ? (
              <button
                className={`chatbot-mic ${isListening ? 'listening' : ''}`}
                onClick={toggleListen}
                disabled={loading}
                title={isListening ? "Arrêter l'écoute" : "Dicter"}
                aria-label="Dicter"
              >
                {isListening ? '🔴' : '🎤'}
              </button>
            ) : (
              <button
                className="chatbot-send"
                onClick={handleSend}
                disabled={loading}
                id="ai-chatbot-send"
                aria-label="Envoyer"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
